/**
 * The Hitlist. The bounty board, and the twenty-four people the city already wants.
 *
 * **THE BOARD IS OPEN.** It was not when this file was last touched, and its header then was a
 * catalogue of what a screen for a system with no server had invented: nine people wearing the
 * portraits of twenty-four authored ones, a live kill contract on a man who chats in the player's
 * own war room, fabricated dollar bounties for a currency flow that did not exist, and eight
 * two-letter grudge codes that `docs/DATA-MODEL.md` then adopted as canon for a database CHECK.
 * Ruling 1l.3 deleted all of it and left the authored roster and the published rules, which were
 * the two things that were true. Phase 8 adds the third.
 *
 * Three tabs, and the split between them is the point:
 *
 *   The Board  LIVE. Real listings, posted in dirty at a 50 percent fee burned on the way in,
 *              claimed by Hospitalize and by nothing else, resolving pro rata ninety seconds
 *              after the punch. Every number arrives from the server.
 *   Wanted     The twenty-four Independents, from `content/cast.json` by direct import, the same
 *              way `game/src/game/eras.ts` reads `content/eras.json`. GAME-DESIGN 7.6: they are
 *              the unaffiliated operators holding the eight NPC blocks and they appear as
 *              house-funded bounties during Sweeps. They are NOT fight-list filler. Mob Wars ships
 *              NPC "Whacked Mobsters" to pad a thin list and that is a confession, not a feature.
 *   Rules      Published, because 1j.12's whole argument is that a rule nobody has to reverse
 *              engineer is a rule nobody writes a spreadsheet about.
 *
 * The 24 names come from the content file by direct import, which carries an exactly-once coverage
 * check over all 24 `pvp.targets.*` portraits, so a name can never drift off its face again.
 */

import { useEffect, useState } from 'react'
import { A } from '../art'
import cast from '../../../content/cast.json'
import { Icon } from '../ui/Icon'
import { Btn, Chip, Col, Panel, PanelHead, Rail, Row, Stack, Tabs } from '../ui/Kit'
import { sz, useViewport } from '../ui/viewport'
import type { KnownAssetId } from '../art'
import type { FightTarget } from '@ashport/shared'
import { clock, useGame } from '../game/state'
import { useSession } from '../game/session'

/** One of CONTENT-BIBLE 3.4's twenty-four, as the content file holds them. */
interface Target {
  id: string
  kind: string
  assetId: string
  name: string
  age: number
  tier?: number
  tierName?: string
  line: string
}

/**
 * The authored twenty-four, highest band first.
 *
 * `tier` is 1 to 6 and orders the board; `tierName` is what the band is CALLED on it, which is
 * canon rather than a label this file picked. Both come straight off the row.
 */
const TARGETS: Target[] = (cast as Target[])
  .filter((c) => c.kind === 'target')
  .sort((a, b) => (b.tier ?? 0) - (a.tier ?? 0) || a.name.localeCompare(b.name))

/** The bands, in the order the board shows them, taken from the rows rather than declared. */
const BANDS = [...new Set(TARGETS.map((t) => t.tierName ?? ''))].filter((x) => x !== '')

const BAND_TINT = ['var(--ink-dim)', 'var(--police)', 'var(--gold)', 'var(--heat)', 'var(--arrest)', 'var(--arrest)']

type TabId = 'live' | 'wanted' | 'rules'

function BoardPanel({ band }: { band: string | null }) {
  const rows = band === null ? TARGETS : TARGETS.filter((t) => t.tierName === band)
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="The Board"
        icon="target"
        sub="Twenty-four names the city already wants"
        right={
          <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
            {rows.length} shown
          </span>
        }
      />
      <div className="scroll-y" style={{ flex: 1, padding: '8px 10px 10px' }}>
        <p style={{ margin: '0 0 10px', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
          The Independents. They hold the eight blocks nobody has taken yet, and the house puts
          money on them during a Sweep. Player paper is on the other tab.
        </p>
        <Col gap={5}>
          {rows.map((t) => (
            <Row
              key={t.id}
              gap={9}
              style={{
                padding: '7px 8px',
                background: 'rgba(255,255,255,.02)',
                border: '1px solid var(--line-soft)',
                alignItems: 'flex-start',
              }}
            >
              {/*
                * The portrait and the name come off the SAME ROW, which is the entire fix. They
                * used to come from two places and disagreed on nine of twenty-four.
                */}
              <img
                src={A(t.assetId as KnownAssetId)}
                alt={t.name}
                style={{ width: 44, height: 54, flex: 'none', objectFit: 'cover', border: '1px solid var(--line)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Row gap={7} style={{ justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{t.name}</span>
                  <Chip c={BAND_TINT[Math.min(5, Math.max(0, (t.tier ?? 1) - 1))]}>{t.tierName}</Chip>
                </Row>
                <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--ink-mid)', lineHeight: 1.45 }}>
                  {t.line}
                </p>
              </div>
            </Row>
          ))}
        </Col>
      </div>
    </Panel>
  )
}


/**
 * THE LIVE BOARD. Real money on real heads, and every number came from the server.
 *
 * Dirty in and dirty out, a 50 percent fee burned at post, 24 hours flat, and 20 percent of an
 * expiry burned with the remaining 80 going TO THE SURVIVOR rather than back to the lister. That
 * last one is 1j.12's single most important line: it converts "permanent punching bag with no
 * counterplay" into the most profitable thing that can happen to a player.
 */
function LiveBoard() {
  // `clock()` takes SECONDS REMAINING and not an absolute instant, which is why this reads the
  // shared tick rather than calling Date.now(): every countdown in the game is derived from server
  // time so a player whose machine is three minutes fast never sees a bar fill early.
  const { now } = useGame()
  const h = useSession((s) => s.hitlist)
  const load = useSession((s) => s.loadHitlist)
  const loadPvp = useSession((s) => s.loadPvp)
  const busy = useSession((s) => s.busy)
  const post = useSession((s) => s.postBounty)
  /*
   * `s.pvp` AND NOT `s.pvp?.targets ?? []`.
   *
   * The second one returns a NEW EMPTY ARRAY on every render while `pvp` is null, so the store's
   * reference check never matches, the component re-renders, and React tears the tree down with
   * "Maximum update depth exceeded". It typechecks, it lints, and it takes the screen out
   * completely. The only thing that found it was rendering the page in a real browser, which is
   * why that is worth doing rather than trusting a build.
   *
   * Select the SLICE, derive below. A selector must return something stable or something already
   * in the store.
   */
  const pvp = useSession((s) => s.pvp)
  const targets = pvp?.targets ?? EMPTY_TARGETS
  const refusal = useSession((s) => s.refusal)
  const clearRefusal = useSession((s) => s.clearRefusal)
  const [who, setWho] = useState('')
  const [amount, setAmount] = useState('')

  useEffect(() => {
    void load().catch(() => undefined)
    // The post form needs the bracket, because a bounty goes on somebody you could have reached
    // yourself. The two reads are independent and neither blocks the other.
    void loadPvp().catch(() => undefined)
  }, [load, loadPvp])

  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead
        title="The Board"
        icon="target"
        sub="Claimed by putting somebody in hospital, and by nothing else"
        right={
          h ? (
            <span className="t-mono" style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
              {h.rows.length} open
            </span>
          ) : undefined
        }
      />
      {h && h.onMe.length > 0 && (
        <Row gap={7} style={{ padding: '7px 10px', background: 'rgba(200,60,60,.08)', alignItems: 'center' }}>
          <Icon n="heat" s={14} c="var(--arrest)" />
          <span style={{ fontSize: 11.5, color: 'var(--ink-hi)', flex: 1 }}>
            There is money on you. Your Heat stops falling until it runs out, and if nobody
            collects, eighty percent of it is yours.
          </span>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--arrest)' }}>
            {money(h.onMe.reduce((n, b) => n + b.amount, 0))}
          </span>
        </Row>
      )}
      <div className="scroll-y" style={{ flex: 1, padding: '8px 10px 10px' }}>
        {/*
          * The post form is here rather than on its own screen because a bounty is a thing you
          * decide while looking at the board, and because the ceiling is a property of the TARGET
          * rather than of the poster: the refusal names their number, so the honest place to learn
          * it is the moment you try.
          */}
        <Row gap={6} style={{ marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={who}
            onChange={(e) => setWho(e.target.value)}
            style={{ background: 'var(--ink-well)', color: 'var(--ink)', padding: '3px 6px', fontSize: 11 }}
          >
            <option value="">Put paper on somebody</option>
            {targets.map((t) => (
              <option key={t.playerId} value={t.playerId}>
                {t.displayName}
              </option>
            ))}
          </select>
          <input
            className="t-mono"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="dollars"
            style={{
              background: 'var(--ink-well)',
              color: 'var(--ink)',
              padding: '3px 6px',
              fontSize: 11,
              width: 96,
            }}
          />
          <Btn
            size="sm"
            v="danger"
            disabled={busy || who === '' || amount === ''}
            onClick={() => void post(who, Number(amount) * 100, '')}
            title="Half again on top, burned"
          >
            Post it
          </Btn>
          {h && (
            <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
              plus {h.rules.feeBp / 100}% burned, {h.rules.hours}h, dirty only
            </span>
          )}
        </Row>

        {/*
          THE REFUSAL, ON THE SCREEN THAT CAUSED IT, and this screen did not have one.

          The comment eight lines up says the ceiling "is a property of the TARGET rather than of
          the poster: the refusal names their number, so the honest place to learn it is the moment
          you try". That argument only works if the refusal is somewhere a player can read it, and
          nothing on the Hitlist read `refusal`. So the whole disclosure went to the console.

          It bites hardest exactly where the design puts the most weight on it. `maxBounty` is
          `0.4 * clean + 1.5 * dirty` on the TARGET, so on a young Era every ceiling is small and
          many are zero, and "There are already two on that head", "You have four out already" and
          "They are not worth that much" were all silent. Pressing Post it did nothing, forever,
          with no reason given.
        */}
        {refusal !== null && (
          <Row
            gap={7}
            style={{
              marginBottom: 10,
              padding: '7px 9px',
              border: '1px solid var(--line)',
              background: 'rgba(200,60,60,.08)',
              alignItems: 'center',
            }}
          >
            <Icon n="warn" s={13} c="var(--arrest)" />
            <span style={{ flex: 1, fontSize: 11.5, color: 'var(--ink-hi)', lineHeight: 1.45 }}>
              {refusal}
            </span>
            <button
              onClick={clearRefusal}
              aria-label="Dismiss"
              style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0 }}
            >
              <Icon n="close" s={11} c="var(--ink-dim)" />
            </button>
          </Row>
        )}

        {!h ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)' }}>Reading the board.</p>
        ) : h.rows.length === 0 ? (
          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.5 }}>
            Nobody has money on them right now. That is either a quiet week or a frightened one.
          </p>
        ) : (
          <Col gap={5}>
            {h.rows.map((r) => (
              <Row
                key={r.id}
                gap={9}
                style={{
                  padding: '7px 8px',
                  background: 'rgba(255,255,255,.02)',
                  border: '1px solid var(--line-soft)',
                  alignItems: 'center',
                }}
              >
                <img
                  src={A(r.tierIconId as KnownAssetId, 'icons.bounty.tier_01_street_mark')}
                  alt=""
                  style={{ width: 22, height: 22, flex: 'none' }}
                />
                <Col gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Row gap={6} style={{ alignItems: 'baseline' }}>
                    <span style={{ fontSize: 12, color: 'var(--ink-hi)' }}>{r.targetName}</span>
                    {r.mine && <Chip>yours, so not yours to claim</Chip>}
                    {r.onMe && <Chip k="heat">this is you</Chip>}
                  </Row>
                  <span style={{ fontSize: sz(10.5), color: 'var(--ink-dim)' }}>
                    posted by {r.posterHandle}, runs out in {clock((r.expiresAt - now) / 1000)}
                  </span>
                </Col>
                <span className="t-mono" style={{ fontSize: 13, color: 'var(--gold)', flex: 'none' }}>
                  {money(r.amount)}
                </span>
              </Row>
            ))}
          </Col>
        )}
      </div>
    </Panel>
  )
}

const money = (c: number) => `$${Math.round(c / 100).toLocaleString('en-US')}`

/** One frozen array, so an empty Fight List is a stable reference rather than a render loop. */
const EMPTY_TARGETS: FightTarget[] = []

/**
 * The rules the board runs on, published where anybody can read them.
 *
 * Every line is ruled and recorded at DESIGN-DELTAS 1j.12 and `docs/PHASE-6B.md` section 11,
 * Phase 8. Publishing them now is not filler: the whole design argument is that everything a
 * userscript would compute must be visible to every account, and a rule a player can read before
 * the system exists is a rule nobody has to reverse engineer afterwards.
 */
function RulesPanel() {
  const rules: [string, string][] = [
    [
      'A bounty is claimed by putting somebody in hospital, and by nothing else',
      'The board is a market in downtime rather than in kills. An intent is not a board item and never carries a printed payout.',
    ],
    [
      'Claims are not first come',
      'The claim resolves ninety seconds after the hospitalization, and every valid claimant inside that window shares pro rata by Fair Fight weight. It is the only formulation a script cannot beat by being faster.',
    ],
    [
      'On expiry, eighty percent pays to the survivor',
      'Twenty-four hours flat, half the post fee burned. That turns being a permanent punching bag with no counterplay into the most profitable thing that can happen to you.',
    ],
    [
      'Everything a script would compute is published to everybody',
      'The hospital timer, the Fair Fight band, the win band, the remaining intents, an armed Ambush, and whether a claim is valid with the reason it is not. None of it is ever a pass perk.',
    ],
    [
      'A vendetta cannot be bought',
      'It may be sworn unilaterally only at a beef deficit of two or more. Otherwise it is an offer the party behind has to accept, which is the only thing stopping a large enough pile of dirty cash buying a higher attack cap on one victim.',
    ],
  ]
  return (
    <Panel style={{ flex: 1, minHeight: 0 }}>
      <PanelHead title="How The Board Works" icon="scanner" sub="Published, never reverse engineered" />
      <div className="scroll-y" style={{ flex: 1, padding: '10px 12px 12px' }}>
        {rules.map(([head, body]) => (
          <div key={head} style={{ marginBottom: 12 }}>
            <div className="t-head" style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: '.1em' }}>
              {head}
            </div>
            <p style={{ margin: '4px 0 0', fontSize: 11.5, color: 'var(--ink-mid)', lineHeight: 1.55 }}>
              {body}
            </p>
          </div>
        ))}
      </div>
    </Panel>
  )
}

/* ------------------------------------------------------------------ screen */

export function Hitlist() {
  const [tab, setTab] = useState<TabId>('live')
  const [band, setBand] = useState<string | null>(null)
  const { stacked } = useViewport()

  return (
    <Stack>
      <Col gap={8} style={stacked ? { flex: 'none', minWidth: 0 } : { flex: 1, minWidth: 0 }}>
        <Panel style={{ flex: 'none' }}>
          <Row gap={14} style={{ padding: '7px 12px 0', flexWrap: 'wrap' }}>
            <Icon n="target" s={26} c="var(--brass-lit)" />
            <div style={{ flex: 'none', paddingBottom: 7 }}>
              <div className="t-display" style={{ fontSize: 21, color: 'var(--ink-hi)' }}>The Hitlist</div>
              <div className="t-label" style={{ fontSize: sz(8.5), marginTop: 2 }}>Bounties and beefs</div>
            </div>
            <div style={{ alignSelf: 'flex-end' }}>
              <Tabs
                items={[
                  { id: 'live' as const, label: 'The Board' },
                  { id: 'wanted' as const, label: 'Wanted' },
                  { id: 'rules' as const, label: 'Rules' },
                ]}
                value={tab}
                onChange={setTab}
              />
            </div>
            <div style={{ flex: 1 }} />
            <Row gap={6} style={{ flex: 'none', paddingBottom: 7 }}>
              <Chip k="dirty">dirty in, dirty out</Chip>
            </Row>
          </Row>
        </Panel>

        {tab === 'live' && <LiveBoard />}
        {tab === 'wanted' && <BoardPanel band={band} />}
        {tab === 'rules' && <RulesPanel />}
      </Col>

      {/*
        * The band filter belongs to the WANTED tab and only to it. It filters the twenty-four
        * authored Independents by their authored tier name, and none of those words means anything
        * on a board of live player listings, which are banded by amount.
        */}
      {tab === 'wanted' && (
      <Rail width={240}>
        <Panel style={{ flex: 'none' }}>
          <PanelHead title="Bands" icon="rank" />
          <div style={{ padding: '6px 10px 10px' }}>
            <div onClick={() => setBand(null)} style={{ cursor: 'pointer' }}>
              <Row gap={7} style={{ padding: '5px 4px', borderBottom: '1px solid var(--line-soft)' }}>
                <span style={{ flex: 1, fontSize: 11.5, color: band === null ? 'var(--gold)' : 'var(--ink)' }}>
                  Every band
                </span>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>{TARGETS.length}</span>
              </Row>
            </div>
            {BANDS.map((b) => {
              const n = TARGETS.filter((t) => t.tierName === b).length
              return (
                <div key={b} onClick={() => setBand(b)} style={{ cursor: 'pointer' }}>
                  <Row gap={7} style={{ padding: '5px 4px', borderBottom: '1px solid var(--line-soft)' }}>
                    <span style={{ flex: 1, fontSize: 11.5, color: band === b ? 'var(--gold)' : 'var(--ink)' }}>{b}</span>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--ink-dim)' }}>{n}</span>
                  </Row>
                </div>
              )
            })}
          </div>
        </Panel>
      </Rail>
      )}
    </Stack>
  )
}
